import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Loader2, Gauge, ShieldCheck, ShieldAlert, Save } from 'lucide-react';
import { DRIVER_TIERS, inputClass } from './caseConstants';

// The driver's own record: how experienced they are, how the rig should be set
// up for them, and whether we hold a signed waiver.
export default function CaseDriverProfileCard({ caseData, profile, onDone }) {
  const [tier, setTier] = useState(profile?.tier || '');
  const [ergonomics, setErgonomics] = useState(profile?.ergonomic_notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const signed = !!profile?.waiver_signed;

  const save = async (patch) => {
    if (!caseData.customer_email) {
      setError('Add an email address to the customer card first.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (profile?.id) {
        await base44.entities.DriverProfile.update(profile.id, patch);
      } else {
        await base44.entities.DriverProfile.create({
          customer_email: caseData.customer_email,
          customer_name: caseData.customer_name || '',
          customer_phone: caseData.customer_phone || '',
          ...patch,
        });
      }
      if (onDone) onDone();
    } catch (e) {
      setError(e?.message || 'Could not save the driver profile.');
    }
    setSaving(false);
  };

  return (
    <div className="border border-white/5 bg-[#111115]">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          <Gauge className="w-3.5 h-3.5 text-primary" />
          <p className="text-white text-[10px] font-bold tracking-widest">DRIVER PROFILE</p>
        </div>
        {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-primary" />}
      </div>

      <div className="px-4 py-3 space-y-3">
        <div>
          <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">EXPERIENCE TIER</p>
          <select
            value={tier}
            onChange={(e) => {
              setTier(e.target.value);
              save({ tier: e.target.value });
            }}
            disabled={saving}
            className={inputClass}
          >
            <option value="">Not set</option>
            {DRIVER_TIERS.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>

        <div>
          <p className="text-white/30 text-[10px] font-bold tracking-widest mb-1.5">
            ERGONOMIC NOTES
          </p>
          <textarea
            value={ergonomics}
            onChange={(e) => setErgonomics(e.target.value)}
            rows={3}
            placeholder="Seat position, FOV, paddle vs manual…"
            className={`${inputClass} resize-none leading-relaxed`}
          />
          {ergonomics !== (profile?.ergonomic_notes || '') && (
            <button
              onClick={() => save({ ergonomic_notes: ergonomics })}
              disabled={saving}
              className="mt-2 inline-flex items-center gap-1.5 border border-primary/40 hover:bg-primary/10 text-primary text-[10px] font-bold tracking-widest px-3 py-1.5 transition-colors disabled:opacity-40"
            >
              <Save className="w-3 h-3" /> SAVE NOTES
            </button>
          )}
        </div>

        <div className="pt-2 border-t border-white/5">
          <button
            onClick={() =>
              save({
                waiver_signed: !signed,
                waiver_signed_at: !signed ? new Date().toISOString() : '',
              })
            }
            disabled={saving}
            className={`w-full flex items-center justify-between gap-2 border px-3 py-2 transition-colors ${
              signed
                ? 'border-green-400/40 bg-green-400/5 text-green-300'
                : 'border-amber-400/40 bg-amber-400/5 text-amber-300'
            }`}
          >
            <span className="flex items-center gap-2 text-[10px] font-bold tracking-widest">
              {signed ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
              WAIVER {signed ? 'SIGNED' : 'PENDING'}
            </span>
            <span className="text-[9px] tracking-wider opacity-60">TAP TO TOGGLE</span>
          </button>
        </div>

        {error && <p className="text-red-400 text-[11px]">{error}</p>}
      </div>
    </div>
  );
}